import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { ContractApiResponse, getContractAmortizationSchedule } from '../../../services/contract-services';
import { hideLoader, showLoader } from '../../../store/reducers/appSlice';
import { openToast } from '../../../store/reducers/toastSlice';
import { convertHTMLToPDF } from '../../../utils/autopay-pdf-generator';
import { trackExport, trackPrint } from '../../../utils/google-analytics';
import { formatCurrency, formatInET, formatToUSDate } from '../../../utils/helper';
import { useTranslation } from '../../../utils/translations';
import { AmortizationDataItem } from '../amortizationSchedule';
import {
    buildAmortizationCsvContent,
    buildAmortizationPdfHtml,
    buildAmortizationXlsxData,
    calculateAmortizationGrandTotals,
    dispatchExportError,
    downloadBlob,
    downloadTextFile,
    findExportButtonElement,
} from '../contractDetailsHelpers';

export const useAmortizationSchedule = (contract: ContractApiResponse | null, isImpersonating: boolean) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Amortization Schedule State
    const [amortizationData, setAmortizationData] = useState<AmortizationDataItem[]>([]);
    const [amortizationLoading, setAmortizationLoading] = useState(false);

    // Amortization Export Format State
    const [exportAmortizationFormat, setExportAmortizationFormat] = useState<string>('');
    const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

    // Fetch Amortization Schedule - Fetch all data at once for client-side pagination
    const fetchAmortizationSchedule = useCallback(async () => {
        if (!contract?.Id) return;
        setAmortizationLoading(true);
        try {
            // Fetch all data with a large limit (10000 should be enough for most cases)
            const response = await getContractAmortizationSchedule(contract.Id, true, 1, 10000);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setAmortizationData(response.data as AmortizationDataItem[]);
            }
        } catch (err) {
            console.error('Error fetching amortization schedule:', err);
        } finally {
            setAmortizationLoading(false);
            dispatch(hideLoader());
        }
    }, [contract?.Id, dispatch]);

    // Print all amortization schedule data
    const handlePrintAmortizationSchedule = async () => {
        if (isImpersonating || !contract?.Id) return;

        try {
            // Fetch all amortization data with a large limit for print
            const response = await getContractAmortizationSchedule(contract.Id, true, 1, 10000);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                const allAmortizationData = response.data;

                if (!allAmortizationData || allAmortizationData.length === 0) {
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message:
                                t('contracts.amortizationSchedule.noData') ||
                                'No Amortization Schedule Data Available to Print',
                            severity: 'warning',
                        }),
                    );
                    return;
                }

                // Transform amortization data using the same logic as export
                const transformedSchedule = getTransformedAmortizationData(
                    allAmortizationData as AmortizationDataItem[],
                );

                // Define columns (same as export)
                const columns = [
                    { id: 'date', label: t('contracts.amortizationSchedule.date') || 'Date' },
                    { id: 'eventType', label: t('contracts.amortizationSchedule.eventType') || 'Source' },
                    { id: 'amount', label: t('contracts.amortizationSchedule.payment') || 'Amount' },
                    { id: 'principal', label: t('contracts.amortizationSchedule.principal') || 'Principal' },
                    { id: 'interest', label: t('contracts.amortizationSchedule.interest') || 'Interest' },
                    { id: 'balance', label: t('contracts.amortizationSchedule.balance') || 'Balance' },
                ];

                // Create HTML table for printing
                const tableRows = transformedSchedule
                    .map(
                        schedule => `
                    <tr${schedule.isTotal ? ' style="font-weight: bold; background-color: #FFF9E6;"' : ''}>
                        <td>${schedule.date}</td>
                        <td>${schedule.eventType}</td>
                        <td>${schedule.amount}</td>
                        <td>${schedule.principal}</td>
                        <td>${schedule.interest}</td>
                        <td>${schedule.balance}</td>
                    </tr>
                `,
                    )
                    .join('');

                const grandTotalAmount = allAmortizationData.reduce((sum: number, item) => {
                    const isTotal = item.EventType?.toLowerCase?.()?.includes('total');
                    return isTotal ? sum + (Number(item.Amount) || 0) : sum;
                }, 0);

                const grandTotalPrincipal = allAmortizationData.reduce((sum: number, item) => {
                    const isTotal = item.EventType?.toLowerCase?.()?.includes('total');
                    return isTotal ? sum + (Number(item.PrincipleAmount) || 0) : sum;
                }, 0);

                const grandTotalInterest = allAmortizationData.reduce((sum: number, item) => {
                    const isTotal = item.EventType?.toLowerCase?.()?.includes('total');
                    return isTotal ? sum + (Number(item.InterestAmount) || 0) : sum;
                }, 0);

                const grandTotalRow = `
                    <tr style="font-weight: bold; background-color: #E8E8E8; border-top: 2px solid #333;">
                        <td colspan="2"><strong>Grand Total</strong></td>
                        <td>${formatCurrency(grandTotalAmount)}</td>
                        <td>${formatCurrency(grandTotalPrincipal)}</td>
                        <td>${formatCurrency(grandTotalInterest)}</td>
                        <td></td>
                    </tr>
                `;

                const tableHeaders = columns.map(col => `<th>${col.label}</th>`).join('');

                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                document.body.appendChild(iframe);

                // Track print event
                trackPrint('Amortization Schedule');

                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                    iframeDoc.write(`
                        <html>
                            <head>
                                <title>Amortization Schedule - ${contract?.AspireId || 'Contract'}</title>
                                <style>
                                    @media print {
                                        @page {
                                            margin: 1cm;
                                        }
                                        body {
                                            margin: 0;
                                            padding: 20px;
                                            font-family: Arial, sans-serif;
                                        }
                                    }
                                    body {
                                        font-family: Arial, sans-serif;
                                        padding: 20px;
                                    }
                                    h1 {
                                        margin-bottom: 20px;
                                        color: #333;
                                    }
                                    table {
                                        width: 100%;
                                        border-collapse: collapse;
                                        margin-top: 20px;
                                    }
                                    th, td {
                                        border: 1px solid #ddd;
                                        padding: 8px;
                                        text-align: left;
                                    }
                                    th {
                                        background-color: #f2f2f2;
                                        font-weight: bold;
                                    }
                                    tr:nth-child(even) {
                                        background-color: #f9f9f9;
                                    }
                                </style>
                            </head>
                            <body>
                                <h1>Amortization Schedule</h1>
                                <p><strong>Contract ID:</strong> ${contract?.AspireId || contract?.Id || 'N/A'}</p>
                                <p><strong>Date:</strong> ${formatInET(new Date())}</p>
                                <table>
                                    <thead>
                                        <tr>${tableHeaders}</tr>
                                    </thead>
                                    <tbody>
                                        ${tableRows}
                                        ${grandTotalRow}
                                    </tbody>
                                </table>
                            </body>
                        </html>
                    `);
                    iframeDoc.close();
                    setTimeout(() => {
                        iframe.contentWindow?.print();
                        setTimeout(() => {
                            document.body.removeChild(iframe);
                        }, 100);
                    }, 250);
                }
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message:
                            t('contracts.amortizationSchedule.noData') ||
                            'No Amortization Schedule Data Available to Print',
                        severity: 'warning',
                    }),
                );
            }
        } catch (err) {
            console.error('Error printing amortization schedule:', err);
            const e = err as { message?: string };
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: e?.message || 'Failed to Print Amortization Schedule. Please Try Again.',
                    severity: 'error',
                }),
            );
        }
    };

    // Helper function to get transformed amortization data
    const getTransformedAmortizationData = useCallback(
        (allAmortizationData: AmortizationDataItem[]) => {
            return allAmortizationData.map(item => {
                const isTotal = item.EventType?.toLowerCase?.()?.includes('total');
                let eventType = item.EventType || 'N/A';

                if (!isTotal) {
                    if (eventType === 'Payment') {
                        eventType = 'Paid';
                    } else if (eventType === 'Scheduled') {
                        eventType = 'Outstanding';
                    } else if (eventType === 'Amount Financed') {
                        eventType = `Loan ${formatCurrency(item.Amount ?? 0)} at ${contract?.Yield}%`;
                    }
                }

                return {
                    date: item.PaymentDate ? formatToUSDate(item.PaymentDate) : 'N/A',
                    eventType: isTotal ? 'Annual Total' : eventType,
                    principal: formatCurrency(item.PrincipleAmount ?? 0),
                    interest: formatCurrency(item.InterestAmount ?? 0),
                    amount: formatCurrency(item.Amount ?? 0),
                    balance: formatCurrency(item.Balance ?? 0),
                    isTotal,
                };
            });
        },
        [contract?.Yield],
    );

    // Export to CSV
    const showNoAmortizationDataToast = useCallback(() => {
        dispatch(
            openToast({
                show: true,
                message:
                    t('contracts.amortizationSchedule.noData') || 'No Amortization Schedule Data Available to Export',
                type: 'warning',
            }),
        );
    }, [dispatch, t]);

    const getAmortizationCsvColumns = useCallback(
        () => [
            { id: 'date', label: t('contracts.amortizationSchedule.date') || 'Date' },
            { id: 'eventType', label: t('contracts.amortizationSchedule.eventType') || 'Event Type' },
            { id: 'amount', label: t('contracts.amortizationSchedule.payment') || 'Payment' },
            { id: 'principal', label: t('contracts.amortizationSchedule.principal') || 'Principal' },
            { id: 'interest', label: t('contracts.amortizationSchedule.interest') || 'Interest' },
            { id: 'balance', label: t('contracts.amortizationSchedule.balance') || 'Balance' },
        ],
        [t],
    );

    const handleExportAmortizationCSV = useCallback(async () => {
        if (isImpersonating || !contract?.Id) return;

        try {
            const response = await getContractAmortizationSchedule(contract.Id, true, 1, 10000);
            const allAmortizationData =
                response.statusCode === 200 && Array.isArray(response.data)
                    ? (response.data as AmortizationDataItem[])
                    : [];

            if (allAmortizationData.length === 0) {
                showNoAmortizationDataToast();
                return;
            }

            const transformedSchedule = getTransformedAmortizationData(allAmortizationData);
            const grandTotals = calculateAmortizationGrandTotals(allAmortizationData);
            const columns = getAmortizationCsvColumns();
            const csvContent = buildAmortizationCsvContent(transformedSchedule, grandTotals, columns);

            downloadTextFile(
                csvContent,
                `amortization-schedule-${new Date().toISOString().split('T')[0]}.csv`,
                'text/csv;charset=utf-8;',
            );

            trackExport('CSV', 'Amortization Schedule');
        } catch (err) {
            console.error('Error exporting amortization schedule to CSV:', err);
            dispatchExportError(dispatch, err, 'Failed to Export Amortization Schedule. Please Try Again.');
        }
    }, [
        contract?.Id,
        isImpersonating,
        dispatch,
        getTransformedAmortizationData,
        showNoAmortizationDataToast,
        getAmortizationCsvColumns,
    ]);

    // Export to XLSX
    const getAmortizationXlsxHeaderLabels = useCallback(
        () => [
            t('contracts.amortizationSchedule.date') || 'Date',
            t('contracts.amortizationSchedule.eventType') || 'Event Type',
            t('contracts.amortizationSchedule.payment') || 'Payment',
            t('contracts.amortizationSchedule.principal') || 'Principal',
            t('contracts.amortizationSchedule.interest') || 'Interest',
            t('contracts.amortizationSchedule.balance') || 'Balance',
        ],
        [t],
    );

    const handleExportAmortizationXLSX = useCallback(async () => {
        if (isImpersonating || !contract?.Id) return;

        try {
            const response = await getContractAmortizationSchedule(contract.Id, true, 1, 10000);
            const allAmortizationData =
                response.statusCode === 200 && Array.isArray(response.data)
                    ? (response.data as AmortizationDataItem[])
                    : [];

            if (allAmortizationData.length === 0) {
                showNoAmortizationDataToast();
                return;
            }

            const transformedSchedule = getTransformedAmortizationData(allAmortizationData);
            const grandTotals = calculateAmortizationGrandTotals(allAmortizationData);
            const headerLabels = getAmortizationXlsxHeaderLabels();
            const wsData = buildAmortizationXlsxData(transformedSchedule, grandTotals, headerLabels);

            const ws = XLSX.utils.aoa_to_sheet(wsData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Amortization Schedule');
            XLSX.writeFile(wb, `amortization-schedule-${new Date().toISOString().split('T')[0]}.xlsx`);

            trackExport('XLSX', 'Amortization Schedule');
        } catch (err) {
            console.error('Error exporting amortization schedule to XLSX:', err);
            dispatchExportError(dispatch, err, 'Failed to Export Amortization Schedule. Please Try Again.');
        }
    }, [
        contract?.Id,
        isImpersonating,
        dispatch,
        getTransformedAmortizationData,
        showNoAmortizationDataToast,
        getAmortizationXlsxHeaderLabels,
    ]);

    // Export to PDF
    const handleExportAmortizationPDF = useCallback(async () => {
        if (isImpersonating || !contract?.Id) return;

        try {
            dispatch(showLoader());
            const response = await getContractAmortizationSchedule(contract.Id, true, 1, 10000);
            const allAmortizationData =
                response.statusCode === 200 && Array.isArray(response.data)
                    ? (response.data as AmortizationDataItem[])
                    : [];

            if (allAmortizationData.length === 0) {
                showNoAmortizationDataToast();
                return;
            }

            const transformedSchedule = getTransformedAmortizationData(allAmortizationData);
            const grandTotals = calculateAmortizationGrandTotals(allAmortizationData);
            const columns = getAmortizationCsvColumns();
            const htmlContent = buildAmortizationPdfHtml(transformedSchedule, grandTotals, columns, contract);

            const pdfBlob = await convertHTMLToPDF(htmlContent);

            if (!pdfBlob || pdfBlob.size === 0) {
                throw new Error('PDF generation failed');
            }

            downloadBlob(pdfBlob, `amortization-schedule-${new Date().toISOString().split('T')[0]}.pdf`);

            trackExport('PDF', 'Amortization Schedule');
        } catch (err) {
            console.error('Error exporting amortization schedule to PDF:', err);
            dispatchExportError(dispatch, err, 'Failed to Export Amortization Schedule. Please Try Again.');
        } finally {
            dispatch(hideLoader());
        }
    }, [
        contract,
        isImpersonating,
        dispatch,
        getTransformedAmortizationData,
        showNoAmortizationDataToast,
        getAmortizationCsvColumns,
    ]);

    // Open export dropdown menu
    const handleExportAmortizationSchedule = (event?: React.MouseEvent<HTMLElement> | React.SyntheticEvent) => {
        if (isImpersonating) return;
        const exportButton = findExportButtonElement(event, 'icon-upload');

        if (exportButton) {
            setExportAnchorEl(exportButton);
        }
    };

    // Close export menu
    const handleCloseExportMenu = () => {
        setExportAnchorEl(null);
    };

    // Handle export format selection from menu
    const handleExportFormatSelect = async (format: string) => {
        if (isImpersonating) {
            setExportAnchorEl(null);
            return;
        }
        setExportAnchorEl(null);
        setExportAmortizationFormat(format);
    };

    // Handle export format selection
    useEffect(() => {
        if (!exportAmortizationFormat) return;

        const executeExport = async () => {
            switch (exportAmortizationFormat) {
                case 'CSV':
                    await handleExportAmortizationCSV();
                    break;
                case 'Excel':
                    await handleExportAmortizationXLSX();
                    break;
                case 'PDF':
                    await handleExportAmortizationPDF();
                    break;
                default:
                    break;
            }
            // Reset the format after export
            setExportAmortizationFormat('');
        };

        executeExport();
    }, [
        exportAmortizationFormat,
        handleExportAmortizationCSV,
        handleExportAmortizationXLSX,
        handleExportAmortizationPDF,
    ]);

    return {
        amortizationData,
        amortizationLoading,
        fetchAmortizationSchedule,
        exportAnchorEl,
        handlePrintAmortizationSchedule,
        handleExportAmortizationSchedule,
        handleCloseExportMenu,
        handleExportFormatSelect,
    };
};
